import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { TIPOS_FIDEICOMISO, VERTICALES } from "../lib/catalogs";

export default function FideicomisoNew() {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        numero_fiduciario: "",
        tipo: "garantia",
        proposito: "",
        fecha_constitucion: "",
        vigencia: "",
        notario: "",
        escritura: "",
        verticales_activas: [],
    });

    const toggleVertical = (v) => {
        setForm((f) => ({
            ...f,
            verticales_activas: f.verticales_activas.includes(v)
                ? f.verticales_activas.filter((x) => x !== v)
                : [...f.verticales_activas, v],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/fideicomisos", form);
            toast.success(`Creado ${data.numero_interno}`);
            navigate(`/fideicomisos/${data.id}`);
        } catch (err) {
            toast.error("No se pudo crear", { description: err?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="p-8 lg:p-10 max-w-3xl" data-testid="new-fideicomiso-page">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                Núcleo · Alta
            </div>
            <h1 className="font-display text-4xl tracking-tight">Nuevo fideicomiso</h1>
            <p className="text-sm text-muted-foreground mt-1">
                Se creará en estado <b>En constitución</b>. Podrás activarlo cuando la
                completitud sea ≥ 90%.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="new-fideicomiso-form">
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <Label>Número fiduciario</Label>
                        <Input
                            required
                            placeholder="F/1004"
                            value={form.numero_fiduciario}
                            onChange={(e) => setForm({ ...form, numero_fiduciario: e.target.value })}
                            data-testid="input-numero-fiduciario"
                        />
                    </div>
                    <div>
                        <Label>Tipo</Label>
                        <Select
                            value={form.tipo}
                            onValueChange={(v) => setForm({ ...form, tipo: v })}
                        >
                            <SelectTrigger data-testid="select-tipo">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {TIPOS_FIDEICOMISO.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>
                                        {t.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div>
                    <Label>Propósito</Label>
                    <Textarea
                        required
                        rows={3}
                        value={form.proposito}
                        onChange={(e) => setForm({ ...form, proposito: e.target.value })}
                        data-testid="input-proposito"
                    />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <Label>Fecha de constitución</Label>
                        <Input
                            type="date"
                            required
                            value={form.fecha_constitucion}
                            onChange={(e) => setForm({ ...form, fecha_constitucion: e.target.value })}
                            data-testid="input-fecha-constitucion"
                        />
                    </div>
                    <div>
                        <Label>Vigencia hasta</Label>
                        <Input
                            type="date"
                            value={form.vigencia}
                            onChange={(e) => setForm({ ...form, vigencia: e.target.value })}
                            data-testid="input-vigencia"
                        />
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <Label>Notario</Label>
                        <Input
                            value={form.notario}
                            onChange={(e) => setForm({ ...form, notario: e.target.value })}
                            data-testid="input-notario"
                        />
                    </div>
                    <div>
                        <Label>Escritura pública</Label>
                        <Input
                            value={form.escritura}
                            onChange={(e) => setForm({ ...form, escritura: e.target.value })}
                            data-testid="input-escritura"
                        />
                    </div>
                </div>

                <div>
                    <Label>Verticales activas</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 border border-border rounded-sm p-3">
                        {VERTICALES.map((v) => (
                            <label
                                key={v.value}
                                className="flex items-center gap-2 text-sm cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={form.verticales_activas.includes(v.value)}
                                    onChange={() => toggleVertical(v.value)}
                                    data-testid={`vertical-${v.value}`}
                                />
                                <span>{v.label}</span>
                                {v.value !== "v1_garantia" && (
                                    <span className="badge-mock text-[9px]">próx.</span>
                                )}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => navigate("/fideicomisos")}
                    >
                        Cancelar
                    </Button>
                    <Button type="submit" disabled={busy} data-testid="submit-fideicomiso">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear fideicomiso"}
                    </Button>
                </div>
            </form>
        </div>
    );
}
